import util from 'util';

import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import { Input, ListGroup, ListGroupItem, Label } from 'reactstrap';

import FolderAndPageList from 'app/FolderAndPageList/components/FolderAndPageList';
import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import { MODAL_MESSAGES, REAL_ROOT_FOLDER_ID } from 'lib/const';
import { EN } from 'lib/locales';
import { noop } from 'lib/noop';
import { PUBLISHED } from 'lib/versions';
import { IconButton } from 'ui/Components';
import { addFolder, addPage, folder, parentDirectory } from 'ui/Icons';

import {
  FOLDER_MENU_EDITOR_ACTIONS,
  PAGE_MENU_EDITOR_ACTIONS,
  MENU_ACTION_DELETE,
  MENU_ACTION_DELETE_PAGE,
  MENU_ACTION_MOVE,
  MENU_ACTION_RENAME,
  MENU_ACTION_DUPLICATE,
  MENU_ACTION_FAVORITE,
  MENU_ACTION_UNFAVORITE
} from '../const';
import AddNewPage from './AddNewPage';
import FavoriteFoldersAndPages from './FavoriteFoldersAndPages';
import SelectFolderOrPage from './SelectFolderOrPage';
import styles from './Tree.css';

/**
 * render the contents of a folder and some controls
 */
export default class Tree extends Component {
  static propTypes = {
    addFolder: propTypes.func.isRequired,
    deleteFolder: propTypes.func.isRequired,
    updateFolder: propTypes.func.isRequired,
    deletePage: propTypes.func.isRequired,
    checkStatusAndToggleAddPage: propTypes.func.isRequired,
    createNewUnsavedPage: propTypes.func.isRequired,
    currentPageId: propTypes.string,
    cwd: propTypes.string.isRequired,
    cwdDetails: propTypes.shape({
      Id: propTypes.string,
      Name: propTypes.string,
      Breadcrumb: propTypes.arrayOf(
        propTypes.shape({
          Id: propTypes.string,
          Name: propTypes.string
        })
      ),
      Children: propTypes.arrayOf(
        propTypes.shape({
          Id: propTypes.string,
          Name: propTypes.string,
          Type: propTypes.string
        })
      ),
      DateCreated: propTypes.string,
      DateModified: propTypes.string
    }),
    cwdSiteRootFolderId: propTypes.string,
    duplicatePage: propTypes.func.isRequired,
    favoriteFolders: propTypes.arrayOf(propTypes.shape()),
    favoritePages: propTypes.arrayOf(propTypes.shape()),
    getFolder: propTypes.func,
    isReadonly: propTypes.bool,
    moveContent: propTypes.func.isRequired,
    notifyFailure: propTypes.func.isRequired,
    showAddFolderInput: propTypes.bool.isRequired,
    showAddPageDialog: propTypes.bool,
    setRenderingEnv: propTypes.func.isRequired,
    toggleAddPage: propTypes.func.isRequired,
    toggleFavoriteContent: propTypes.func.isRequired,
    toggleUIAddFolder: propTypes.func.isRequired,
    tree: propTypes.object,
    visitFolder: propTypes.func.isRequired,
    visitPage: propTypes.func.isRequired
  };

  static defaultProps = {
    cwdDetails: {},
    cwdSiteRootFolderId: '',
    currentPageId: undefined,
    favoriteFolders: [],
    favoritePages: [],
    getFolder: noop,
    isReadonly: true,
    showAddPageDialog: false,
    tree: {}
  };

  constructor(props) {
    super(props);

    // TODO add folder input can probably be moved to internal state
    this.state = {
      showDeleteFolder: false,
      showMoveContent: false,
      showRenameFolder: false,
      showDeletePage: false,
      selectedContent: {},
      newFolderName: ''
    };
    this.addFolderInput = {};

    this.handleAddFolderInput = this.handleAddFolderInput.bind(this);
    this.handleDeleteFolder = this.handleDeleteFolder.bind(this);
    this.toggleDeleteFolder = this.toggleDeleteFolder.bind(this);
    this.handleDeletePage = this.handleDeletePage.bind(this);
    this.toggleDeletePage = this.toggleDeletePage.bind(this);
    this.handleMoveContent = this.handleMoveContent.bind(this);
    this.toggleMoveContent = this.toggleMoveContent.bind(this);
    this.toggleRenameFolder = this.toggleRenameFolder.bind(this);
    this.handleRenameFolder = this.handleRenameFolder.bind(this);
    this.handleRenameInputChange = this.handleRenameInputChange.bind(this);
    this.onItemSelect = this.onItemSelect.bind(this);
    this.onActionSelect = this.onActionSelect.bind(this);
    this.setActions = this.setActions.bind(this);
  }

  /**
   * moves content(page or folder) from one folder to another
   * @param {object} parentFolder - object containing id and name of the new parent folder
   */
  handleMoveContent(parentFolder) {
    const { selectedContent } = this.state;
    const { moveContent, cwdDetails, notifyFailure } = this.props;

    this.toggleMoveContent();
    if (selectedContent.id && get(parentFolder, 'id')) {
      moveContent(cwdDetails.Id, selectedContent, parentFolder);
    } else {
      notifyFailure(
        `Cannot move the ${selectedContent.type} because id for the selected folder is not received. Move aborted.`
      );
    }
    this.setState({ selectedContent: {} });
  }

  /**
   * updates folder with changed name
   */
  handleRenameFolder() {
    const { selectedContent, newFolderName } = this.state;
    const { updateFolder, cwd } = this.props;
    if (newFolderName.length && newFolderName !== selectedContent.name) {
      updateFolder(selectedContent.id, newFolderName, cwd);
    }
    this.toggleRenameFolder();
  }

  /**
   * deletes the selected folder
   */
  handleDeleteFolder() {
    const { selectedContent } = this.state;
    const { deleteFolder, cwd } = this.props;
    deleteFolder(selectedContent.id, selectedContent.name, cwd);
    this.toggleDeleteFolder();
  }

  /**
   * deletes the selected page
   */
  handleDeletePage() {
    const { selectedContent } = this.state;
    const { deletePage, cwd } = this.props;
    deletePage(selectedContent.id, selectedContent.name, cwd);
    this.toggleDeletePage();
  }

  /**
   * handle events on the add folder input
   * @param {object} event - the event object
   */
  handleAddFolderInput(event) {
    const { addFolder, cwd, cwdDetails, notifyFailure, toggleUIAddFolder } = this.props;

    switch (event.key) {
      case 'Enter':
        if (cwdDetails?.Children?.some(({ Name }) => this.addFolderInput.value === Name)) {
          notifyFailure(
            `cannot add '${this.addFolderInput.value}' because it already exists here`
          );
        } else {
          addFolder(this.addFolderInput.value.trim(), cwd);
        }

        this.addFolderInput.value = '';
        toggleUIAddFolder();
        break;
      case 'Escape':
        this.addFolderInput.value = '';
        toggleUIAddFolder();
        break;

      // no default
    }
  }

  /**
   * change folder name in state
   * @param {object} event - event containing the folder name
   */
  handleRenameInputChange(event) {
    const newFolderName = event.target.value.trim();
    this.setState({ newFolderName });
  }

  /**
   * toggles visible sections
   * @param {object} child - child object on which action is selected
   * @param {string} action - selected action
   */
  onActionSelect(child, action) {
    const { duplicatePage, toggleFavoriteContent } = this.props;
    switch (action) {
      case MENU_ACTION_DELETE.action: {
        this.setState({
          selectedContent: {
            id: child.Id,
            name: child.Name
          },
          showDeleteFolder: true
        });
        break;
      }
      case MENU_ACTION_DELETE_PAGE.action: {
        this.setState({
          selectedContent: {
            id: child.Id,
            name: child.Name
          },
          showDeletePage: true
        });
        break;
      }
      case MENU_ACTION_MOVE.action: {
        this.setState({
          selectedContent: {
            id: child.Id,
            name: child.Name,
            type: child.Type
          },
          showMoveContent: true
        });
        break;
      }
      case MENU_ACTION_RENAME.action: {
        this.setState({
          selectedContent: {
            id: child.Id,
            name: child.Name
          },
          showRenameFolder: true
        });
        break;
      }
      case MENU_ACTION_DUPLICATE.action: {
        duplicatePage(child.Id, child.Version || PUBLISHED);
        break;
      }
      case MENU_ACTION_FAVORITE.action: {
        toggleFavoriteContent(false, child.Name, child.Type, child.Id, EN);
        break;
      }
      case MENU_ACTION_UNFAVORITE.action: {
        toggleFavoriteContent(true, child.Name, child.Type, child.Id, EN);
        break;
      }
      default:
        break;
    }
  }

  /**
   * returns function to be executed on selection
   * @param {object} child - selected child object (folder ot page)
   */
  onItemSelect(child) {
    const { visitFolder, visitPage } = this.props;
    if (child.Type === 'folder') {
      visitFolder(child.Id);
    } else {
      visitPage(child.Id, child.Version);
    }
  }

  /**
   * returns whether current folder is the real root folder
   * @returns {boolean} true when cwd matches real root folder id, otherwise false
   */
  get isOnRealRoot() {
    return this.props.cwd === REAL_ROOT_FOLDER_ID;
  }

  /**
   * sets child actions based on whether folder or page and whether is favorite
   * @param {object} child - page or folder data
   * @returns {Array} new actions with added favorite/unfavorite
   */
  setActions(child) {
    const { isReadonly } = this.props;
    let actions;
    if (this.isPage(child)) {
      actions = isReadonly ? [] : PAGE_MENU_EDITOR_ACTIONS;
      if (child.Live || child.IsDoNotArchive || child.Scheduled) {
        actions = actions.map(f =>
          f.action === MENU_ACTION_DELETE_PAGE.action ? { ...f, disabled: true } : f
        );
      }
    } else {
      actions = isReadonly ? [] : FOLDER_MENU_EDITOR_ACTIONS;
    }
    const favAction = this.isFavorite(child) ? MENU_ACTION_UNFAVORITE : MENU_ACTION_FAVORITE;
    return [...actions, favAction];
  }

  /**
   * is child a favorite
   * @param {object} child - page or folder data
   * @returns {boolean} yes or no
   */
  isFavorite(child) {
    const { favoriteFolders, favoritePages } = this.props;
    const fav = this.isPage(child) ? favoritePages : favoriteFolders;

    return fav.some(f => f.Id === child.Id);
  }

  /**
   * does child object represent a page
   * @param {object} child - page or folder data
   * @returns {boolean} yes or no
   */
  isPage(child) {
    return child.Type === 'page';
  }

  /**
   * toggles delete folder modal
   */
  toggleDeleteFolder() {
    this.setState(ps => ({ showDeleteFolder: !ps.showDeleteFolder }));
  }

  /**
   * toggles delete page modal
   */
  toggleDeletePage() {
    this.setState(ps => ({ showDeletePage: !ps.showDeletePage }));
  }

  /**
   * toggles rename folder modal
   */
  toggleRenameFolder() {
    this.setState(ps => ({ showRenameFolder: !ps.showRenameFolder, newFolderName: '' }));
  }

  /**
   * toggles move content modal
   */
  toggleMoveContent() {
    this.setState(ps => ({ showMoveContent: !ps.showMoveContent }));
  }

  /**
   * renders modal for rename folder confirmation.
   * @returns {Component} component to show a modal for confirming delete folder action.
   */
  renderRenameFolderModal() {
    const { showRenameFolder, selectedContent, newFolderName } = this.state;
    return (
      <ConfirmationModal
        headerText={util.format(
          MODAL_MESSAGES.CONFIRMATION_HEADER_MESSAGE,
          MODAL_MESSAGES.RENAME_LABEL,
          MODAL_MESSAGES.FOLDER_LABEL
        )}
        isOpen={showRenameFolder}
        onNoClick={this.toggleRenameFolder}
        noButtonLabel={MODAL_MESSAGES.CANCEL_LABEL}
        onYesClick={this.handleRenameFolder}
        yesButtonLabel={MODAL_MESSAGES.RENAME_LABEL}
      >
        <div className="p-1">
          <Fragment>
            <Label>
              {util.format(
                MODAL_MESSAGES.RENAME_CONTENT_MESSAGE,
                MODAL_MESSAGES.FOLDER_LABEL,
                `"${selectedContent.name}"`
              )}
            </Label>
          </Fragment>
          <br />
          <br />
          <Fragment>
            <Label className="font-weight-bold">Enter new name:</Label>
            <Input
              className="w-50"
              name="renameFolder"
              type="text"
              value={newFolderName || selectedContent.name}
              onChange={this.handleRenameInputChange}
            />
          </Fragment>
        </div>
      </ConfirmationModal>
    );
  }

  /**
   * renders modal for delete folder confirmation.
   * @returns {Component} component to show a modal for confirming delete folder action.
   */
  renderDeleteFolderModal() {
    const { showDeleteFolder, selectedContent } = this.state;
    return (
      <ConfirmationModal
        headerText={util.format(
          MODAL_MESSAGES.CONFIRMATION_HEADER_MESSAGE,
          MODAL_MESSAGES.DELETE_LABEL,
          MODAL_MESSAGES.FOLDER_LABEL
        )}
        isOpen={showDeleteFolder}
        onNoClick={this.toggleDeleteFolder}
        noButtonLabel={MODAL_MESSAGES.CANCEL_LABEL}
        onYesClick={this.handleDeleteFolder}
        yesButtonLabel={MODAL_MESSAGES.DELETE_LABEL}
      >
        <div className="p-1">
          <Label>
            {util.format(
              MODAL_MESSAGES.DELETE_CONTENT_MESSAGE,
              MODAL_MESSAGES.FOLDER_LABEL,
              `"${selectedContent.name}"`
            )}
          </Label>
        </div>
      </ConfirmationModal>
    );
  }

  /**
   * renders modal for delete page confirmation.
   * @returns {Component} component to show a modal for confirming delete page action.
   */
  renderDeletePageModal() {
    const { showDeletePage, selectedContent } = this.state;
    return (
      <ConfirmationModal
        headerText={util.format(
          MODAL_MESSAGES.CONFIRMATION_HEADER_MESSAGE,
          MODAL_MESSAGES.DELETE_LABEL,
          MODAL_MESSAGES.CONTENT_LABEL
        )}
        isOpen={showDeletePage}
        onNoClick={this.toggleDeletePage}
        noButtonLabel={MODAL_MESSAGES.CANCEL_LABEL}
        onYesClick={this.handleDeletePage}
        yesButtonLabel={MODAL_MESSAGES.DELETE_LABEL}
      >
        <div className="p-1">
          <Label>
            {util.format(
              MODAL_MESSAGES.DELETE_CONTENT_MESSAGE,
              MODAL_MESSAGES.CONTENT_LABEL,
              `"${selectedContent.name}"`
            )}
          </Label>
        </div>
      </ConfirmationModal>
    );
  }

  render() {
    const {
      checkStatusAndToggleAddPage,
      createNewUnsavedPage,
      currentPageId,
      cwd,
      cwdDetails,
      cwdSiteRootFolderId,
      favoriteFolders,
      favoritePages,
      getFolder,
      isReadonly,
      showAddFolderInput,
      showAddPageDialog,
      visitFolder,
      toggleAddPage,
      toggleUIAddFolder,
      setRenderingEnv,
      tree
    } = this.props;
    const { showMoveContent, showDeleteFolder, showDeletePage, showRenameFolder } = this.state;

    // safely unpack cwd details with defaults to loading state
    const cwdName = cwdDetails?.Name ?? 'Loading...';
    const cwdParentFolderId = (cwdDetails?.Breadcrumb ?? [])
      .map(({ Id }) => Id)
      .slice(-1)
      .pop();

    return (
      <div>
        <ListGroup className={styles.list}>
          <FavoriteFoldersAndPages
            favoriteFolders={favoriteFolders}
            favoritePages={favoritePages}
            onActionSelect={this.onActionSelect}
            onItemSelect={this.onItemSelect}
          />
          <ListGroupItem className={styles.topItem}>
            {cwdParentFolderId && (
              <div
                role="presentation"
                onClick={() => visitFolder(cwdParentFolderId)}
                className={classnames('d-flex align-items-center', styles.listElem)}
                data-test-id="tree-parent-directory"
              >
                <IconButton alt="Parent Directory" icon={parentDirectory} noMargin rem={2} />
                <span className={`${styles.parentDir} align-items-left`}>
                  (Parent Directory)
                </span>
              </div>
            )}
            <div className="mt-2">
              <div className="d-flex flex-row justify-content-end">
                <div className="d-flex flex-row mr-auto">
                  <IconButton alt="Folder" icon={folder} rem={2.3} noMargin />
                  <span className={styles.cwdName} title={cwdName}>
                    {cwdName}
                  </span>
                </div>
                {!this.isOnRealRoot && (
                  <React.Fragment>
                    <div
                      className={classnames(
                        'd-flex flex-column align-items-center',
                        styles.addFolder
                      )}
                    >
                      <IconButton
                        alt="Add New Folder"
                        icon={addFolder}
                        isReadonly={isReadonly}
                        noMargin
                        rem={2.3}
                        onClick={toggleUIAddFolder}
                        data-test-id="tree-addNewFolder"
                      />
                    </div>
                    <div
                      className={classnames(
                        'd-flex flex-column align-items-center',
                        styles.addPage
                      )}
                    >
                      <IconButton
                        alt="Add New Page"
                        icon={addPage}
                        isReadonly={isReadonly}
                        noMargin
                        rem={2.3}
                        onClick={checkStatusAndToggleAddPage}
                        data-test-id="tree-addNewPage"
                      />
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
          </ListGroupItem>
          <ListGroupItem hidden={!showAddFolderInput}>
            <Input
              autoFocus
              placeholder="new folder"
              onKeyDown={this.handleAddFolderInput}
              innerRef={input => {
                if (input) {
                  input.focus();
                  this.addFolderInput = input;
                }
              }}
            />
          </ListGroupItem>
          <FolderAndPageList
            currentPageId={currentPageId}
            foldersAndPages={cwdDetails.Children}
            isOnRealRoot={this.isOnRealRoot}
            onItemSelect={this.onItemSelect}
            onActionSelect={this.onActionSelect}
            setActions={this.setActions}
          />
        </ListGroup>
        {showAddPageDialog && (
          <AddNewPage
            createNewUnsavedPage={createNewUnsavedPage}
            isOpen={showAddPageDialog}
            onAddPageClick={toggleAddPage}
            cwd={cwd}
            setRenderingEnv={setRenderingEnv}
          />
        )}
        {showMoveContent && (
          <SelectFolderOrPage
            folderTree={tree}
            getFolder={getFolder}
            initialFolderId={cwdSiteRootFolderId}
            isOpen={showMoveContent}
            onCancel={this.toggleMoveContent}
            onEnter={this.handleMoveContent}
          />
        )}
        {showDeleteFolder && this.renderDeleteFolderModal()}
        {showDeletePage && this.renderDeletePageModal()}
        {showRenameFolder && this.renderRenameFolderModal()}
      </div>
    );
  }
}
